import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon, Badge, Button, Drawer, Group, Loader, NumberInput, Paper, Select, Stack, Table, Text, Title, Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconEdit, IconPlus, IconShoppingCartPlus, IconTrash } from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useItems } from '../items/itemsApi';
import { useSuppliers } from '../purchasing/purchasingApi';
import { useBranches } from '../branches/branchesApi';
import {
  useReorderRules, useReorderSuggestions, useUpsertReorderRule, useDeleteReorderRule,
  type ReorderRuleDto,
} from './inventoryReportsApi';

const qty = (n: number) => new Intl.NumberFormat('ar-PS', { maximumFractionDigits: 3, numberingSystem: 'latn' }).format(n);

export function ReorderPage() {
  const { t, i18n } = useTranslation();
  const ar = i18n.language === 'ar';
  const suggestions = useReorderSuggestions();
  const rules = useReorderRules();
  const upsert = useUpsertReorderRule();
  const del = useDeleteReorderRule();
  const { data: items } = useItems();
  const { data: suppliers } = useSuppliers();
  const { data: branches } = useBranches();

  const [drawer, setDrawer] = useState(false);
  const [editing, setEditing] = useState<ReorderRuleDto | null>(null);
  const [deleting, setDeleting] = useState<ReorderRuleDto | null>(null);

  const itemOptions = (items ?? []).map((i) => ({ value: i.itemId, label: `${ar ? i.nameAr : i.nameEn ?? i.nameAr} — ${i.sku}` }));
  const supplierOptions = (suppliers ?? []).map((s) => ({ value: s.supplierId, label: ar ? s.nameAr : s.nameEn ?? s.nameAr }));
  const branchOptions = (branches ?? []).map((b) => ({ value: b.branchId, label: ar ? b.nameAr : b.nameEn ?? b.nameAr }));

  const form = useForm({
    initialValues: { itemId: '', branchId: null as string | null, reorderLevel: 10, reorderQty: 20, preferredSupplierId: null as string | null },
    validate: { itemId: (v) => (v ? null : t('common.errors.required')) },
  });

  const openCreate = () => {
    setEditing(null);
    form.setValues({ itemId: '', branchId: null, reorderLevel: 10, reorderQty: 20, preferredSupplierId: null });
    form.resetDirty();
    setDrawer(true);
  };
  const openEdit = (r: ReorderRuleDto) => {
    setEditing(r);
    form.setValues({ itemId: r.itemId, branchId: r.branchId, reorderLevel: r.reorderLevel, reorderQty: r.reorderQty, preferredSupplierId: r.preferredSupplierId });
    form.resetDirty();
    setDrawer(true);
  };

  const submit = form.onSubmit(async (v) => {
    try {
      await upsert.mutateAsync({
        itemId: v.itemId, branchId: v.branchId, reorderLevel: v.reorderLevel, reorderQty: v.reorderQty,
        preferredSupplierId: v.preferredSupplierId,
      });
      notifications.show({ color: 'green', title: t('common.toasts.updated'), message: t('inventory.reorder.ruleSaved') });
      setDrawer(false);
    } catch (err) {
      notifications.show({
        color: 'red', title: t('common.toasts.errorTitle'),
        message: (isAxiosError(err) && (err.response?.data?.detail as string)) || t('common.toasts.errorBody'),
      });
    }
  });

  const sugg = suggestions.data ?? [];
  const ruleRows = rules.data ?? [];

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.operations')}
        title={t('inventory.reorder.title')}
        subtitle={t('inventory.reorder.subtitle')}
        actions={<Button leftSection={<IconPlus size={16} stroke={1.8} />} onClick={openCreate}>{t('inventory.reorder.addRule')}</Button>}
      />

      {/* Suggestions — the "what to buy now" report */}
      <div>
        <Group gap="xs" mb="sm">
          <IconShoppingCartPlus size={18} stroke={1.8} color="var(--wheels-red, #B5191D)" />
          <Title order={5} c="var(--wheels-ink-strong)">{t('inventory.reorder.suggestionsTitle')}</Title>
          {sugg.length > 0 && <Badge color="red" variant="light" radius="sm">{sugg.length}</Badge>}
        </Group>
        <Paper p={sugg.length ? 0 : 'lg'}>
          {suggestions.isLoading ? (
            <Stack align="center" py={32}><Loader size="sm" /></Stack>
          ) : sugg.length === 0 ? (
            <Text ta="center" c="var(--wheels-ink-muted)" py="lg">{t('inventory.reorder.suggestionsEmpty')}</Text>
          ) : (
            <Table verticalSpacing="sm" highlightOnHover striped="even">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('inventory.reorder.item')}</Table.Th>
                  <Table.Th>{t('inventory.reorder.supplier')}</Table.Th>
                  <Table.Th style={{ textAlign: 'end' }}>{t('inventory.reorder.onHand')}</Table.Th>
                  <Table.Th style={{ textAlign: 'end' }}>{t('inventory.reorder.level')}</Table.Th>
                  <Table.Th style={{ textAlign: 'end' }}>{t('inventory.reorder.suggested')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sugg.map((s) => (
                  <Table.Tr key={`${s.itemId}-${s.branchId ?? 'all'}`}>
                    <Table.Td>
                      <Text fw={500} c="var(--wheels-ink-strong)">{ar ? s.itemNameAr : s.itemNameEn ?? s.itemNameAr}</Text>
                      <Text size="xs" c="var(--wheels-ink-muted)" className="font-numeric font-mono">{s.itemSku}{(ar ? s.branchNameAr : s.branchNameEn ?? s.branchNameAr) ? ` · ${ar ? s.branchNameAr : s.branchNameEn ?? s.branchNameAr}` : ''}</Text>
                    </Table.Td>
                    <Table.Td c="var(--wheels-ink-muted)">{s.preferredSupplierName ?? '—'}</Table.Td>
                    <Table.Td className="font-numeric" style={{ textAlign: 'end', color: 'var(--wheels-red, #B5191D)', fontWeight: 600 }}>{qty(s.onHand)}</Table.Td>
                    <Table.Td className="font-numeric" style={{ textAlign: 'end' }} c="var(--wheels-ink-muted)">{qty(s.reorderLevel)}</Table.Td>
                    <Table.Td className="font-numeric" style={{ textAlign: 'end', fontWeight: 600 }}>{qty(s.suggestedQty)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      </div>

      {/* Rules management */}
      <div>
        <Title order={5} c="var(--wheels-ink-strong)" mb="sm">{t('inventory.reorder.rulesTitle')}</Title>
        <Paper p={ruleRows.length ? 0 : 'lg'}>
          {rules.isLoading ? (
            <Stack align="center" py={32}><Loader size="sm" /></Stack>
          ) : ruleRows.length === 0 ? (
            <EmptyState icon={<IconShoppingCartPlus size={22} stroke={1.6} />} title={t('inventory.reorder.rulesEmpty')}
              description={t('inventory.reorder.rulesEmptyBody')} actionLabel={t('inventory.reorder.addRule')} onAction={openCreate} />
          ) : (
            <Table verticalSpacing="sm" highlightOnHover striped="even">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('inventory.reorder.item')}</Table.Th>
                  <Table.Th>{t('inventory.reorder.branch')}</Table.Th>
                  <Table.Th style={{ textAlign: 'end' }}>{t('inventory.reorder.level')}</Table.Th>
                  <Table.Th style={{ textAlign: 'end' }}>{t('inventory.reorder.orderQty')}</Table.Th>
                  <Table.Th>{t('inventory.reorder.supplier')}</Table.Th>
                  <Table.Th style={{ width: 100, textAlign: 'end' }}>{t('common.actions')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {ruleRows.map((r) => (
                  <Table.Tr key={r.reorderRuleId}>
                    <Table.Td>
                      <Text fw={500} c="var(--wheels-ink-strong)">{ar ? r.itemNameAr : r.itemNameEn ?? r.itemNameAr}</Text>
                      <Text size="xs" c="var(--wheels-ink-muted)" className="font-numeric font-mono">{r.itemSku}</Text>
                    </Table.Td>
                    <Table.Td c="var(--wheels-ink-muted)">{r.branchId ? branchOptions.find((b) => b.value === r.branchId)?.label ?? '—' : t('inventory.reorder.allBranches')}</Table.Td>
                    <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>{qty(r.reorderLevel)}</Table.Td>
                    <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>{qty(r.reorderQty)}</Table.Td>
                    <Table.Td c="var(--wheels-ink-muted)">{r.preferredSupplierName ?? '—'}</Table.Td>
                    <Table.Td>
                      <Group gap="xs" justify="flex-end" wrap="nowrap">
                        <Tooltip label={t('common.edit')} withinPortal>
                          <ActionIcon variant="subtle" color="gray" onClick={() => openEdit(r)} aria-label={t('common.edit')}><IconEdit size={16} stroke={1.6} /></ActionIcon>
                        </Tooltip>
                        <Tooltip label={t('common.delete')} withinPortal>
                          <ActionIcon variant="subtle" color="red" onClick={() => setDeleting(r)} aria-label={t('common.delete')}><IconTrash size={16} stroke={1.6} /></ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      </div>

      <Drawer opened={drawer} onClose={() => setDrawer(false)} position={ar ? 'left' : 'right'} size="md" padding="lg"
        title={editing ? t('inventory.reorder.editRule') : t('inventory.reorder.addRule')}>
        <form onSubmit={submit}>
          <Stack gap="md">
            <Select label={t('inventory.reorder.item')} data={itemOptions} searchable withAsterisk
              disabled={!!editing} {...form.getInputProps('itemId')} />
            <Select label={t('inventory.reorder.branch')} data={branchOptions} clearable searchable
              placeholder={t('inventory.reorder.allBranches')} disabled={!!editing} {...form.getInputProps('branchId')} />
            <Group grow>
              <NumberInput label={t('inventory.reorder.level')} min={0} decimalScale={3} {...form.getInputProps('reorderLevel')} />
              <NumberInput label={t('inventory.reorder.orderQty')} min={0} decimalScale={3} {...form.getInputProps('reorderQty')} />
            </Group>
            <Select label={t('inventory.reorder.supplier')} data={supplierOptions} clearable searchable
              placeholder={t('inventory.reorder.noSupplier')} {...form.getInputProps('preferredSupplierId')} />
            <Group justify="flex-end" gap="sm" mt="sm">
              <Button variant="default" onClick={() => setDrawer(false)}>{t('common.cancel')}</Button>
              <Button type="submit" loading={upsert.isPending}>{t('common.save')}</Button>
            </Group>
          </Stack>
        </form>
      </Drawer>

      <ConfirmDialog opened={deleting !== null} onClose={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          try { await del.mutateAsync(deleting.reorderRuleId); setDeleting(null); }
          catch { notifications.show({ color: 'red', title: t('common.toasts.errorTitle'), message: t('common.toasts.errorBody') }); }
        }}
        title={t('inventory.reorder.deleteTitle')} message={t('inventory.reorder.deleteBody')} loading={del.isPending} />
    </Stack>
  );
}
